/**
 * menuPermissions.ts - 菜单权限检查逻辑
 *
 * 功能说明：
 * - 菜单权限过滤逻辑
 * - 权限检查函数
 * - 递归过滤菜单项
 *
 * 依赖关系：
 * - 依赖 store/authStore.ts 的权限状态
 *
 * 关键注释索引：
 * - [权限检查] - 见下方 checkPermission 函数
 * - [菜单过滤] - 见下方 filterMenuItems 函数
 *
 * 创建日期：2026-05-03
 * 作者：AI Assistant
 */

import type { MenuProps } from 'antd';

type MenuItem = Required<MenuProps>['items'][number];

/**
 * [菜单配置类型] 菜单项配置类型定义（带权限）
 */
export type MenuItemConfig = Omit<MenuItem, 'children'> & {
  permission?: string | string[] | null;
  children?: MenuItemConfig[];
};

/**
 * [权限检查] 检查用户是否满足菜单权限要求
 *
 * @description 支持多种权限匹配规则：
 * - 字符串权限直接匹配
 * - 数组权限任一匹配
 * - 子资源 manage 覆盖 view
 * - 父资源 manage 覆盖子资源
 * - system:manage 超级管理员覆盖一切
 *
 * @param permission - 权限要求（字符串、数组或无要求）
 * @param userPermissions - 用户拥有的权限列表
 * @returns 是否满足权限要求
 */
export const checkPermission = (
  permission: string | string[] | null | undefined,
  userPermissions: string[]
): boolean => {
  // 无权限要求，允许访问
  if (!permission) return true;

  const perms = Array.isArray(permission) ? permission : [permission];
  return perms.some(p => {
    // [直接匹配] 直接匹配权限字符串
    if (userPermissions.includes(p)) return true;

    const parts = p.split(':');
    const resource = parts[0];
    const subResource = parts.length === 3 ? parts[1] : null;

    // [子资源管理] 检查子资源 manage 覆盖（如 report:cost:manage 覆盖 report:cost:view）
    if (subResource && userPermissions.includes(`${resource}:${subResource}:manage`)) {
      return true;
    }

    // [父资源管理] 检查父资源 manage 覆盖（如 report:manage 覆盖 report:cost:view）
    if (userPermissions.includes(`${resource}:manage`)) {
      return true;
    }

    // [超级管理员] system:manage 覆盖一切
    if (userPermissions.includes('system:manage')) {
      return true;
    }

    return false;
  });
};

/**
 * [菜单过滤] 递归过滤菜单项
 *
 * @description 根据用户权限过滤菜单：
 * - 检查菜单项的权限要求
 * - 递归过滤子菜单
 * - 如果子菜单全部被过滤，则不显示父菜单
 *
 * @param items - 菜单配置列表
 * @param userPermissions - 用户权限列表
 * @returns 过滤后的菜单项列表
 */
export const filterMenuItems = (items: MenuItemConfig[], userPermissions: string[]): MenuItem[] => {
  return items
    .filter(item => checkPermission(item.permission, userPermissions))
    .map(item => {
      if (item.children) {
        const filteredChildren = filterMenuItems(item.children, userPermissions);
        // 如果子菜单全部被过滤掉，则不显示父菜单
        if (filteredChildren.length === 0) {
          return null;
        }
        return {
          ...item,
          children: filteredChildren
        };
      }
      return item;
    })
    .filter(Boolean) as MenuItem[];
};
